import { Component } from '@opten/compiler/metadata/component';
import { ComponentRefs } from '@opten/compiler/metadata/di';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import { BehaviorSubject, Observable, of as observableOf, of } from 'rxjs';
import { catchError, distinctUntilChanged, first, switchMap, tap } from 'rxjs/operators';
import { distinctUntilUserChanged } from '../../utilities/observable.helper';
import { AccountService } from '../account/account.service';
import { ProfileService } from '../account/profile.service';
import { AuthService } from '../auth/auth.service';
import { BasketQuantityComponent } from '../basket/basket-quantity';
import { LocalizationService } from '../core/localization.service';
import { HttpClient } from '../httpclient/httpclient';
import { isTokenExpired } from '../jwt/is-token-expired';
import { TokenHandler } from '../jwt/token.handler';
import { Basket } from '../models/basket';
import { User } from '../models/customer';
import { canWriteShop, isPartner } from '../models/roles';
import { OrderService } from '../order/order.service';
import { BasketStorage, BASKET_VERSION } from '../storage/basket-storage';
import { MemberStorage } from '../storage/member-storage';
import { OrderStorage } from '../storage/order-storage';
import { TokenStorage } from '../storage/token-storage';
import { WantsToBeGuestStorage } from '../storage/wants-to-be-guest.storage';
import { ShopService } from './shop.service';

export let orderService: OrderService;
export let authService: AuthService;
export let tokenStorage: TokenStorage;
export let memberStorage: MemberStorage;
export let orderStorage: OrderStorage;
export let accountService: AccountService;
export let profileService: ProfileService;
export let localizationService: LocalizationService;
export let shopService: ShopService;
export let basketStorage: BasketStorage;
export let wantsToBeGuestStorage: WantsToBeGuestStorage;
export let httpClient: HttpClient;

@Component({ className: 'js-shop-context' })
export class ShopContext implements OnInit {
	onAuthenticationChanged: BehaviorSubject<User> = new BehaviorSubject<User>(null);
	onIsGuest: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	@ComponentRefs(BasketQuantityComponent) basketQuantityComponents: BasketQuantityComponent[];

	onInit(): void {
		httpClient = new HttpClient();
		authService = new AuthService();
		tokenStorage = new TokenStorage();
		memberStorage = new MemberStorage();
		basketStorage = new BasketStorage();
		orderStorage = new OrderStorage();
		wantsToBeGuestStorage = new WantsToBeGuestStorage();
		accountService = new AccountService();
		profileService = new ProfileService();
		localizationService = new LocalizationService();
		orderService = new OrderService();
		shopService = new ShopService();
		basketStorage.onReinitBasketFull.subscribe(basket => this.basketUpdated(basket));
		basketStorage
			.get()
			.pipe(
				first(),
				switchMap(basket => (basket && basket.version !== BASKET_VERSION ? basketStorage.clearBasket() : of(basket)))
			)
			.subscribe(basket => this.basketUpdated(basket));

		this.completeContext()
			.pipe(
				switchMap(user => accountService.isGuestUser(user)),
				catchError(error => {
					tokenStorage.clear();
					memberStorage.clear();

					return this.completeContext().pipe(switchMap(user => accountService.isGuestUser(user)));
				}),
				distinctUntilChanged((isGuestA, isGuestB) => isGuestA === isGuestB)
			)
			.subscribe(isGuest => this.onIsGuest.next(isGuest));

		this.onAuthenticationChanged
			.pipe(
				distinctUntilUserChanged(),
				switchMap(user => accountService.isGuestUser(user)),
				distinctUntilChanged((isGuestA, isGuestB) => isGuestA === isGuestB)
			)
			.subscribe(isGuest => this.onIsGuest.next(isGuest));
	}

	finalizeBasket(): Observable<any> {
		return basketStorage.reinitBasket().pipe(first());
	}

	moveBasketFromGuestToUser(user: User): void {
		basketStorage.get().subscribe(basket => {
			if (basket && basket.items && user && user.contractualPartner) {
				basket.items.forEach(item => {
					item.location = {
						id: user.contractualPartner.multiLocations[0].locations[0].id
					};
				});
				basket.centralWarehouseAddress = user.contractualPartner.multiLocations[0].locations[0].addresses[0];
				basket.centralWarehouseAddress.addresses = user.contractualPartner.multiLocations[0].locations[0].id;
				basket.customerName = user.name;
				basket.isGuest = !isPartner(user);

				basketStorage.set(basket);
			}
		});
	}

	resetOrders(): Observable<any> {
		return orderStorage.clearOrders().pipe(first());
	}

	resetToken(): void {
		tokenStorage.clear();
	}

	basketUpdated(basket: Basket, notification = false) {
		this.basketQuantityComponents.forEach(basketQuantityComponent =>
			basketQuantityComponent.updateBasketQuantity(basket, notification)
		);
	}

	completeContext(): Observable<User> {
		//test if already authenticated else login with guest account
		const tokenHandler = new TokenHandler();
		return tokenHandler.get().pipe(
			switchMap(token => {
				if (token && isTokenExpired(token) === false) {
					return memberStorage.get().pipe(
						switchMap(user => {
							if (user && canWriteShop(user)) {
								return accountService.get().pipe(
									switchMap(user => {
										memberStorage.set(user);
										return observableOf(user);
									})
								);
							} else if (isPartner(user)) {
								wantsToBeGuestStorage.set(true);
								return accountService.get().pipe(
									switchMap(user => {
										memberStorage.set(user);
										return observableOf(user);
									})
								);
							} else {
								return this.authenticateGuest();
							}
						})
					);
				} else {
					return this.authenticateGuest();
				}
			}),
			tap(user => {
				this.onAuthenticationChanged.next(user);
			})
		);
	}

	authenticateGuest(): Observable<User> {
		return accountService.getGuestUserAuth().pipe(
			switchMap(request => {
				return authService.authenticate(request);
			}),
			switchMap(user => {
				basketStorage.reinitBasket();
				this.onAuthenticationChanged.next(user);
				return observableOf(user);
			})
		);
	}
}
